// Single-series dynamic bar chart with item abbreviations on the X axis
// and a side legend mapping abbreviation -> full item name.

import { useTheme, Box, Typography } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import { tokens } from "../theme";

/**
 * data shape:
 *   [{ abbr: "BE", name: "beer", salesPrice: 1273400 }, ...]
 */
const currency = (v) =>
  (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

export default function BarChart({
  data = [],
  isDashboard = false,
  rounded = 4,
  legendWidth = 150, // side legend width
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Teal similar to your screenshot
  const barColor = "#64C3BC";

  return (
    <Box display="flex" height="100%">
      {/* Chart area */}
      <Box flex={1}>
        <ResponsiveBar
          data={data}
          keys={["salesPrice"]}
          indexBy="abbr" // X-axis labels (capital abbreviations)
          // IMPORTANT: small right margin now that the legend is separate,
          // so the plot area is wide and bars have room.
          margin={{ top: 40, right: 20, bottom: 50, left: 80 }}
          // Increase padding to create clear space between bars
          padding={0.45}
          innerPadding={8}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={() => barColor}
          borderRadius={rounded}
          enableGridX={false}
          enableGridY={true}
          valueFormat={currency}
          theme={{
            axis: {
              domain: { line: { stroke: colors.grey[100] } },
              legend: { text: { fill: colors.grey[100] } },
              ticks: {
                line: { stroke: colors.grey[100], strokeWidth: 1 },
                text: { fill: colors.grey[100] },
              },
            },
            grid: { line: { stroke: colors.grey[700], strokeWidth: 1 } },
            legends: { text: { fill: colors.grey[100] } },
            tooltip: { container: { color: colors.grey[900] } },
          }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 8,
            tickRotation: 0,
            legend: isDashboard ? undefined : "Items (abbrev.)",
            legendPosition: "middle",
            legendOffset: 36,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: isDashboard ? undefined : "Sales Price",
            legendPosition: "middle",
            legendOffset: -60,
          }}
          enableLabel={false}
          labelSkipWidth={12}
          labelSkipHeight={12}
          borderColor={{ from: "color", modifiers: [["darker", 1.2]] }}
          legends={[]} // single series -> no built-in legend
          tooltip={({ indexValue, value, color }) => (
            <div style={{ padding: 8, background: "white", border: `1px solid ${color}` }}>
              <div><strong>{indexValue}</strong></div>
              <div>Sales: {currency(value)}</div>
            </div>
          )}
          animate
          motionConfig="gentle"
          role="application"
        />
      </Box>

      {/* Side legend (abbr -> full name) */}
      <Box
        width={legendWidth}
        ml={2}
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="flex-start"
      >
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            alignItems: "center",
            mb: 1,
            mt: 1,
          }}
        >
          <Box
            sx={{
              width: 16,
              height: 16,
              bgcolor: barColor,
              borderRadius: "3px",
              mr: 1,
            }}
          />
          <Typography variant="body2" color={colors.grey[100]}>
            Items
          </Typography>
        </Box>

        {data.map((d) => (
          <Typography key={d.abbr} variant="body2" sx={{ color: colors.grey[100], mb: 0.5 }}>
            {d.abbr} — {d.name}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
